/**
 * Pointer / gesture layer for the play surface (PLAN §2.5). Owns the voice ⇄ zone map that the play screen
 * reads each frame. Pointer Events only; `touch-action: none` + setPointerCapture on the surface.
 */
import { pitchSpecHz, pitchSpecSemitones, saHz, type Family, type PitchSpec, type Settings, type TuningContext } from '@tonist/shared';
import type { VoiceHandle } from '../engine/types';
import { engineStarted, ensureEngineStarted, type EngineWithExtras, type RendererWithExtras } from '../lib/engineBoot';
import { audioLog } from '../engine/log';
import { hitTest, zoneKey, type MandalaLayout, type RingIndex, type ZoneHit } from './geometry';
import {
  DOUBLE_TAP_MS, LONG_PRESS_MS, LONG_PRESS_VELOCITY_FLOOR, PrayerWheel, VelocitySmoother, detectTwoFingerSwipe, isDoubleTap, normaliseVelocity,
  wheelToNudge, type TapRecord,
} from './pointerMath';
import { autoOctaveFor, consonanceGuard, glideSeconds, idleEvolutionDue, noteOnOptions, retunePlan, stepIdleWalk, voiceToSteal } from '../music/voiceRules';
import { droneIdForZone } from '../music/assignment';

export interface TrackedVoice {
  id: number;
  handle: VoiceHandle;
  droneId: string;
  ring: RingIndex;
  index: number;
  /** zone angle (radians, CCW from east) so the drone assignment can be re-evaluated later */
  angle: number;
  pitch: PitchSpec;
  slot: number;
  startTime: number;
  family: Family;
  /** Play-mode latch (double-tap / Alt) or Shift-held; continuous voices are always persistent */
  latched: boolean;
  held: boolean;
  continuous: boolean;
  /** −18 dB octave-below partner rule applies */
  autoOctave: boolean;
  /** idle-evolution state */
  walk: { bendCents: number; brightness: number };
  /** the bindu root drone is exempt from the voice cap */
  exempt: boolean;
}

export interface PointerDeps {
  engine: EngineWithExtras;
  getRenderer(): RendererWithExtras | null;
  getLayout(): MandalaLayout;
  getTuning(): TuningContext;
  getSettings(): Pick<Settings, 'mode' | 'assignment' | 'droneIds' | 'tonicPc' | 'scaleId'>;
  familyOf(droneId: string): Family;
  /** Called on the very first interaction, after engine.start() resolves. */
  onFirstTouch(): void;
  setTonic(pc: number): void;
  stepTonic(dir: number, bySemitone: boolean): void;
  stepScale(dir: number): void;
  selectScaleIndex(i: number): void;
  toggleMode(): void;
  onMacroNudge(kind: 'brightness' | 'width', delta: number): void;
  onVoicesChanged?(): void;
}

interface ActivePointer {
  id: number;
  x0: number; y0: number; x: number; y: number; t0: number;
  isTouch: boolean;
  zone: ZoneHit;
  /** voice this pointer drives (Play: its own; Continuous: the drone's persistent voice) */
  voiceId: number | null;
  lastSt: number;
  velocity: VelocitySmoother;
  longPress: boolean;
  longPressTimer: ReturnType<typeof setTimeout> | null;
  moved: boolean;
  consumed: boolean;
  fireRing: boolean;
  pendingMove: PointerEvent | null;
}

const MAX_SLOTS = 16;
const TAP_MOVE_PX = 10;

export class PointerLayer {
  readonly voices = new Map<number, TrackedVoice>();
  readonly wheel = new PrayerWheel();
  private pointers = new Map<number, ActivePointer>();
  private slots = new Array<boolean>(MAX_SLOTS).fill(false);
  private lastTap: TapRecord | null = null;
  private lastTouchMs = performance.now();
  private lastWalkMs = 0;
  private raf = 0;
  private el: HTMLElement | null = null;
  private firstTouchDone = false;
  private detach: Array<() => void> = [];

  constructor(private readonly deps: PointerDeps) {}

  // ------------------------------------------------------------ lifecycle
  attach(el: HTMLElement): void {
    this.el = el;
    const on = <K extends keyof HTMLElementEventMap>(k: K, fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions) => {
      el.addEventListener(k, fn, opts); this.detach.push(() => el.removeEventListener(k, fn, opts));
    };
    on('pointerdown', this.onDown);
    on('pointermove', this.onMove);
    on('pointerup', this.onUp);
    on('pointercancel', this.onUp);
    on('lostpointercapture', this.onUp);
    on('wheel', this.onWheel, { passive: false });
    on('contextmenu', (e) => e.preventDefault());
    on('dblclick', (e) => e.preventDefault());
    const key = (e: KeyboardEvent) => this.onKey(e);
    window.addEventListener('keydown', key);
    this.detach.push(() => window.removeEventListener('keydown', key));
    // A mouse button released outside the window (or while another window has focus) can arrive without a pointerup.
    // Losing focus or being hidden releases every pointer voice so nothing is left sounding with no finger on it.
    const dropAll = () => { if (this.pointers.size) { audioLog.warn('pointer', `window lost focus with ${this.pointers.size} pointer(s) down: releasing`); this.releaseAllPointers(); } };
    const onVisibility = () => { if (document.visibilityState === 'hidden') dropAll(); };
    window.addEventListener('blur', dropAll);
    document.addEventListener('visibilitychange', onVisibility);
    this.detach.push(() => { window.removeEventListener('blur', dropAll); document.removeEventListener('visibilitychange', onVisibility); });
  }

  dispose(): void {
    for (const d of this.detach) d();
    this.detach = [];
    if (this.raf) cancelAnimationFrame(this.raf);
    for (const p of this.pointers.values()) if (p.longPressTimer) clearTimeout(p.longPressTimer);
    this.pointers.clear();
    this.el = null;
  }

  get activePointerCount(): number { return this.pointers.size; }
  get lastTouch(): number { return this.lastTouchMs; }

  // ------------------------------------------------------------ pointer events
  private onDown = (e: PointerEvent): void => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const el = this.el; if (!el) return;
    e.preventDefault();
    try { el.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    const now = performance.now();
    this.lastTouchMs = now;
    const { x, y } = this.local(e);
    const zone = this.hit(x, y);
    audioLog.log('pointer', () => `down #${e.pointerId} ${e.pointerType} at ${x.toFixed(0)},${y.toFixed(0)} → ring ${zone.ring} index ${zone.index}${zone.pitch ? ` degree ${zone.pitch.degree} oct ${zone.pitch.octave} bend ${zone.pitch.bendCents.toFixed(0)}c` : zone.pitchClass !== undefined ? ` pitch class ${zone.pitchClass}` : ''} radius ${zone.radiusInRing.toFixed(2)}${e.altKey ? ' alt' : ''}${e.shiftKey ? ' shift' : ''} engine ${engineStarted() ? 'running' : 'not started'}`);
    const p: ActivePointer = {
      id: e.pointerId, x0: x, y0: y, x, y, t0: now, isTouch: e.pointerType === 'touch', zone, voiceId: null, lastSt: 0,
      velocity: new VelocitySmoother(), longPress: false, longPressTimer: null, moved: false, consumed: false, fireRing: zone.ring === 4, pendingMove: null,
    };
    p.velocity.reset(x, y, now);
    this.pointers.set(e.pointerId, p);
    this.deps.getRenderer()?.pointerDown(e.pointerId, x, y);

    // The engine must be running before the first voice; afterwards handle the touch synchronously.
    if (engineStarted()) { this.handleDown(p, zone, e.altKey, e.shiftKey, now); return; }
    const first = !this.firstTouchDone;
    this.firstTouchDone = true;
    // A fire-ring tap needs no audio: apply the tonic change right away so a quick first tap is never lost.
    if (zone.ring === 4) {
      this.wheel.spin(zone.angle, now);
      if (zone.pitchClass !== undefined) this.changeTonic(zone.pitchClass);
    }
    ensureEngineStarted(this.deps.engine).then(() => {
      if (first) { this.deps.onFirstTouch(); this.deps.getRenderer()?.pulse('first-touch'); }
      if (zone.ring === 4) return; // already handled above
      const stillDown = this.pointers.get(p.id) === p;
      // A quick tap that ended before the engine came up still sounds: start the note, then let it fade.
      if (!stillDown) this.pointers.set(p.id, p);
      this.handleDown(p, zone, e.altKey, e.shiftKey, performance.now());
      if (!stillDown) { this.pointers.delete(p.id); if (!p.consumed) this.releasePointerVoice(p, false); this.deps.onVoicesChanged?.(); }
    }).catch(() => { if (first) this.firstTouchDone = false; });
  };

  private handleDown(p: ActivePointer, zone: ZoneHit, alt: boolean, shift: boolean, now: number): void {
    const { x, y } = p;
    // three-finger tap: clear all latched
    if (p.isTouch && this.pointers.size === 3) {
      const all = [...this.pointers.values()];
      if (all.every((q) => now - q.t0 < DOUBLE_TAP_MS)) {
        for (const q of all) { q.consumed = true; this.releasePointerVoice(q, true); }
        this.releaseAll();
        return;
      }
    }

    if (zone.ring === 4) {
      // fire ring: tap sets tonic; drag spins the prayer wheel
      this.wheel.spin(zone.angle, now);
      if (zone.pitchClass !== undefined) this.changeTonic(zone.pitchClass);
      return;
    }

    const tap: TapRecord = { zoneKey: zoneKey(zone.ring, zone.index), time: now, x, y };
    const dbl = isDoubleTap(this.lastTap, tap);
    this.lastTap = dbl ? null : tap;

    p.longPressTimer = setTimeout(() => { p.longPress = true; p.longPressTimer = null; this.applyGesture(p, now + LONG_PRESS_MS); }, LONG_PRESS_MS);

    const mode = this.deps.getSettings().mode;
    if (mode === 'continuous') this.downContinuous(p, zone, dbl);
    else this.downPlay(p, zone, dbl, alt, shift);
    this.deps.onVoicesChanged?.();
  }

  private onMove = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    p.pendingMove = e;
    if (!this.raf) this.raf = requestAnimationFrame(this.flushMoves);
  };

  private flushMoves = (): void => {
    this.raf = 0;
    const now = performance.now();
    for (const p of this.pointers.values()) {
      const e = p.pendingMove; if (!e) continue; p.pendingMove = null;
      // a mouse move with no button held means the pointerup was lost (released outside the window): treat as up
      if (!p.isTouch && e.pointerType === 'mouse' && e.buttons === 0) { audioLog.warn('pointer', `#${e.pointerId} moving with no button held: pointerup was lost, releasing`); this.onUp(e); continue; }
      const { x, y } = this.local(e);
      p.x = x; p.y = y;
      const dist = Math.hypot(x - p.x0, y - p.y0);
      if (dist > TAP_MOVE_PX) { p.moved = true; if (p.longPressTimer) { clearTimeout(p.longPressTimer); p.longPressTimer = null; } }
      p.velocity.push(x, y, now);
      this.lastTouchMs = now;
      this.deps.getRenderer()?.pointerMove(e.pointerId, x, y);
      if (p.consumed) continue;

      // two-finger horizontal swipe = scale change
      if (p.isTouch && this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        if (a && b && !a.consumed && !b.consumed) {
          const dir = detectTwoFingerSwipe(a, b);
          if (dir !== 0) {
            a.consumed = b.consumed = true;
            this.releasePointerVoice(a, true); this.releasePointerVoice(b, true);
            this.deps.stepScale(dir);
            this.deps.getRenderer()?.pulse('scale-change');
            continue;
          }
        }
      }

      const zone = this.hit(x, y);
      if (p.fireRing) { if (zone.ring === 4) this.wheel.spin(zone.angle, now); continue; }
      if (zone.ring === 4) continue; // dragged off the mandala: keep the last pitch
      const v = p.voiceId !== null ? this.voices.get(p.voiceId) : undefined;
      if (!v) continue;
      const prev = v.pitch;
      if (zone.pitch && (zone.pitch.degree !== prev.degree || zone.pitch.octave !== prev.octave || zone.pitch.bendCents !== prev.bendCents)) {
        this.glideVoice(v, zone);
      }
      p.zone = zone;
      this.applyGesture(p, now);
    }
  };

  private onUp = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    this.pointers.delete(e.pointerId);
    audioLog.log('pointer', () => `${e.type} #${e.pointerId} voice ${p.voiceId ?? 'none'}${p.fireRing ? ' (fire ring)' : ''}${p.consumed ? ' consumed' : ''}`);
    if (p.longPressTimer) clearTimeout(p.longPressTimer);
    const { x, y } = this.local(e);
    this.deps.getRenderer()?.pointerUp(e.pointerId, x, y);
    this.lastTouchMs = performance.now();
    if (p.fireRing) { this.wheel.release(); return; }
    if (p.consumed) return;
    this.releasePointerVoice(p, false);
    this.deps.onVoicesChanged?.();
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const delta = wheelToNudge(e.deltaY, e.deltaMode);
    this.deps.onMacroNudge(e.ctrlKey || e.metaKey ? 'width' : 'brightness', delta);
  };

  private onKey(e: KeyboardEvent): void {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    switch (e.key) {
      case 'Escape': this.releaseAll(); break;
      case 'ArrowLeft': this.deps.stepTonic(-1, e.shiftKey); break;
      case 'ArrowRight': this.deps.stepTonic(1, e.shiftKey); break;
      case 'ArrowUp': this.deps.stepScale(-1); this.deps.getRenderer()?.pulse('scale-change'); break;
      case 'ArrowDown': this.deps.stepScale(1); this.deps.getRenderer()?.pulse('scale-change'); break;
      case ' ': if (t === document.body || t === this.el) { this.deps.toggleMode(); } else return; break;
      default: {
        if (/^[0-9]$/.test(e.key)) { this.deps.selectScaleIndex(e.key === '0' ? 9 : Number(e.key) - 1); this.deps.getRenderer()?.pulse('scale-change'); break; }
        return;
      }
    }
    e.preventDefault();
  }

  // ------------------------------------------------------------ voice creation
  private downPlay(p: ActivePointer, zone: ZoneHit, dbl: boolean, alt: boolean, shift: boolean): void {
    const existing = this.voiceInZone(zone.ring, zone.index);
    if (dbl || alt) {
      if (existing?.latched && existing.handle.latched) {
        // unlatch → release
        existing.latched = false;
        existing.handle.latch(false);
        this.deps.getRenderer()?.pulse('unlatch');
        p.consumed = true;
        return;
      }
      const v = this.createPlayVoice(p, zone);
      if (v) { v.latched = true; v.handle.latch(true); v.autoOctave = autoOctaveFor(this.deps.getTuning().scale, v.pitch.degree, true); this.deps.getRenderer()?.pulse('latch'); }
      return;
    }
    if (shift) {
      if (existing?.held) { existing.held = false; existing.handle.latch(false); p.consumed = true; return; }
      const v = this.createPlayVoice(p, zone);
      if (v) { v.held = true; v.handle.latch(true); }
      return;
    }
    this.createPlayVoice(p, zone);
  }

  private createPlayVoice(p: ActivePointer, zone: ZoneHit): TrackedVoice | null {
    if (!zone.pitch) return null;
    const tuning = this.deps.getTuning();
    const settings = this.deps.getSettings();
    const droneId = droneIdForZone(settings, zone.ring, zone.angle);
    // consonance guard against sounding (non-releasing) voices, third voice and up
    const sounding = [...this.voices.values()].filter((v) => !v.handle.releasing).map((v) => pitchSpecSemitones(tuning, v.pitch));
    const guard = consonanceGuard(tuning.scale, zone.pitch.degree, zone.pitch.octave, sounding);
    const pitch: PitchSpec = guard.nudged ? { ...zone.pitch, degree: guard.degree } : zone.pitch;
    // voice cap
    const steal = voiceToSteal([...this.voices.values()].filter((v) => !v.handle.releasing).map((v) => ({ id: v.id, latched: v.latched || v.held || v.continuous, startTime: v.startTime, exempt: v.exempt })));
    if (steal !== null) { const sv = this.voices.get(steal); if (sv) { sv.latched = sv.held = false; sv.handle.latch(false); sv.handle.release(); } }
    const hz = pitchSpecHz(tuning, pitch);
    const velocity = normaliseVelocity(p.velocity.value);
    const gesture = { radius: zone.radiusInRing, velocity };
    const handle = this.deps.engine.noteOn(droneId, hz, gesture, noteOnOptions(velocity));
    const v = this.track(handle, droneId, zone, pitch, false);
    p.voiceId = v.id;
    p.lastSt = pitchSpecSemitones(tuning, pitch);
    return v;
  }

  private downContinuous(p: ActivePointer, zone: ZoneHit, dbl: boolean): void {
    if (!zone.pitch) return;
    const settings = this.deps.getSettings();
    const tuning = this.deps.getTuning();
    const droneId = droneIdForZone(settings, zone.ring, zone.angle);
    const current = [...this.voices.values()].find((v) => v.continuous && v.droneId === droneId);
    if (current && !dbl && current.ring === zone.ring && current.index === zone.index) {
      // tapping the zone the drone is already on toggles it off
      this.deps.engine.stopContinuous(droneId);
      this.untrack(current.id);
      p.consumed = true;
      return;
    }
    const hz = pitchSpecHz(tuning, zone.pitch);
    const gesture = { radius: zone.radiusInRing, velocity: normaliseVelocity(p.velocity.value) };
    const handle = this.deps.engine.setContinuous(droneId, hz, gesture);
    let v = this.voices.get(handle.id);
    if (!v) v = this.track(handle, droneId, zone, zone.pitch, true);
    else { v.ring = zone.ring; v.index = zone.index; v.pitch = zone.pitch; }
    if (dbl) { v.latched = true; handle.latch(true); this.deps.getRenderer()?.pulse('latch'); }
    p.voiceId = v.id;
    p.lastSt = pitchSpecSemitones(tuning, zone.pitch);
  }

  private track(handle: VoiceHandle, droneId: string, zone: ZoneHit, pitch: PitchSpec, continuous: boolean): TrackedVoice {
    const slot = this.allocSlot();
    const v: TrackedVoice = {
      id: handle.id, handle, droneId, ring: zone.ring, index: zone.index, angle: zone.angle, pitch, slot, startTime: performance.now(),
      family: this.deps.familyOf(droneId), latched: false, held: false, continuous, autoOctave: false,
      walk: { bendCents: 0, brightness: zone.radiusInRing }, exempt: zone.ring === 0,
    };
    this.voices.set(v.id, v);
    return v;
  }

  private untrack(id: number): void {
    const v = this.voices.get(id);
    if (!v) return;
    this.slots[v.slot] = false;
    this.voices.delete(id);
  }

  private allocSlot(): number {
    const i = this.slots.indexOf(false);
    if (i >= 0) { this.slots[i] = true; return i; }
    return MAX_SLOTS - 1;
  }

  private glideVoice(v: TrackedVoice, zone: ZoneHit): void {
    if (!zone.pitch) return;
    const tuning = this.deps.getTuning();
    const st = pitchSpecSemitones(tuning, zone.pitch);
    const prevSt = pitchSpecSemitones(tuning, v.pitch);
    v.handle.setPitch(pitchSpecHz(tuning, zone.pitch), glideSeconds(st - prevSt));
    v.pitch = zone.pitch;
    v.ring = zone.ring; v.index = zone.index;
    v.walk.bendCents = 0;
  }

  private applyGesture(p: ActivePointer, _now: number): void {
    const v = p.voiceId !== null ? this.voices.get(p.voiceId) : undefined;
    if (!v) return;
    let velocity = normaliseVelocity(p.velocity.value);
    if (p.longPress) velocity = Math.max(velocity, LONG_PRESS_VELOCITY_FLOOR);
    v.handle.setGesture({ radius: p.zone.radiusInRing, velocity });
    v.walk.brightness = p.zone.radiusInRing;
  }

  /**
   * The drone assignment changed (selector): every persistent voice — continuous or latched — moves to the drone its
   * zone now maps to, at the same pitch, so choosing another drone while something plays is heard immediately.
   */
  migrateDrones(): void {
    const settings = this.deps.getSettings();
    for (const v of Array.from(this.voices.values())) {
      if (!(v.continuous || v.latched) || v.handle.releasing) continue;
      const target = droneIdForZone(settings, v.ring, v.angle);
      if (target === v.droneId) continue;
      const hz = v.handle.hz;
      const gesture = { radius: v.walk.brightness, velocity: 0 };
      audioLog.log('voice', `drone change: moving voice #${v.id} (${v.continuous ? 'continuous' : 'latched'}) ${v.droneId} → ${target} at ${hz.toFixed(1)}Hz`);
      let handle: VoiceHandle;
      if (v.continuous) {
        this.deps.engine.stopContinuous(v.droneId);
        handle = this.deps.engine.setContinuous(target, hz, gesture);
      } else {
        v.handle.latch(false);
        handle = this.deps.engine.noteOn(target, hz, gesture, { attackScale: 0.5 });
        handle.latch(true);
      }
      this.voices.delete(v.id);
      const moved: TrackedVoice = { ...v, id: handle.id, handle, droneId: target, family: this.deps.familyOf(target) };
      this.voices.set(moved.id, moved);
      for (const p of this.pointers.values()) if (p.voiceId === v.id) p.voiceId = moved.id;
    }
    this.deps.onVoicesChanged?.();
  }

  /** Release every pointer-held voice (not latched ones) and forget the pointers; used when the window loses focus. */
  releaseAllPointers(): void {
    for (const p of this.pointers.values()) {
      if (p.longPressTimer) clearTimeout(p.longPressTimer);
      if (p.fireRing) { this.wheel.release(); continue; }
      if (!p.consumed) this.releasePointerVoice(p, false);
    }
    this.pointers.clear();
    this.deps.onVoicesChanged?.();
  }

  private releasePointerVoice(p: ActivePointer, force: boolean): void {
    if (p.voiceId === null) return;
    const v = this.voices.get(p.voiceId);
    p.voiceId = null;
    if (!v) return;
    if (!force && (v.latched || v.held || v.continuous)) return;
    if (v.continuous) { this.deps.engine.stopContinuous(v.droneId); this.untrack(v.id); return; }
    v.handle.latch(false);
    v.handle.release();
  }

  private voiceInZone(ring: RingIndex, index: number): TrackedVoice | undefined {
    for (const v of this.voices.values()) if (v.ring === ring && v.index === index && !v.handle.releasing) return v;
    return undefined;
  }

  // ------------------------------------------------------------ public actions
  releaseAll(): void {
    for (const v of this.voices.values()) { v.latched = v.held = false; if (v.continuous) this.deps.engine.stopContinuous(v.droneId); }
    this.deps.engine.releaseAll();
    for (const id of [...this.voices.keys()]) { const v = this.voices.get(id); if (v?.continuous) this.untrack(id); }
    this.deps.onVoicesChanged?.();
  }

  /** Fire-ring tap / arrow keys: set the tonic and retune latched + continuous + held voices (lowest first, staggered). */
  changeTonic(pc: number): void {
    const before = this.deps.getSettings().tonicPc;
    if (pc === before) return;
    audioLog.log('pointer', `tonic ${before} → ${pc}`);
    this.deps.setTonic(pc); // the play screen's tuning effect calls retuneAll(true)
    this.deps.getRenderer()?.pulse('tonic-change');
  }

  /** Re-derive every persistent voice's Hz from the current tuning (tonic, scale or JI changed). */
  retuneAll(stagger: boolean): void {
    const tuning = this.deps.getTuning();
    this.deps.engine.setTonicHz(saHz(tuning.tonicPc));
    const n = tuning.scale.semitones.length;
    const persistent = [...this.voices.values()].filter((v) => (v.latched || v.held || v.continuous || this.isPointerHeld(v.id)) && !v.handle.releasing);
    for (const v of persistent) {
      // keep the degree if it still exists in the new scale; else fold onto Sa' (the ring's closing petal)
      if (v.pitch.degree > n) { v.pitch = { ...v.pitch, degree: n }; v.index = n; }
    }
    const plan = retunePlan(persistent.map((v) => ({ id: v.id, hz: v.handle.hz })));
    for (const step of plan) {
      const v = this.voices.get(step.id); if (!v) continue;
      const hz = pitchSpecHz(tuning, v.pitch);
      const apply = () => { if (this.voices.has(v.id)) v.handle.setPitch(hz, step.glideSec); };
      if (stagger && step.delaySec > 0) setTimeout(apply, step.delaySec * 1000); else apply();
    }
  }

  private isPointerHeld(voiceId: number): boolean {
    for (const p of this.pointers.values()) if (p.voiceId === voiceId) return true;
    return false;
  }

  /**
   * Per-frame tick from the play screen: drops voices the engine has freed, decays gesture velocity, steps the
   * prayer wheel, and runs idle evolution. Returns the number of sounding (non-releasing) voices.
   */
  tick(nowMs: number, dtSec: number): number {
    if (engineStarted()) {
      const live = new Set(this.deps.engine.voices().map((v) => v.id));
      for (const id of [...this.voices.keys()]) if (!live.has(id)) this.untrack(id);
    }
    for (const p of this.pointers.values()) {
      if (p.voiceId === null) continue;
      const before = p.velocity.value;
      p.velocity.idle(nowMs);
      if (Math.abs(before - p.velocity.value) > 1) this.applyGesture(p, nowMs);
    }
    const omega = this.wheel.step(dtSec);
    this.deps.getRenderer()?.setPrayerWheel?.(omega);

    if (this.pointers.size === 0 && idleEvolutionDue(this.lastTouchMs, nowMs) && nowMs - this.lastWalkMs > 250) {
      this.lastWalkMs = nowMs;
      const tuning = this.deps.getTuning();
      for (const v of this.voices.values()) {
        if (!(v.latched || v.continuous || v.held) || v.handle.releasing) continue;
        v.walk = stepIdleWalk(v.walk, Math.random, 0.25);
        v.handle.setPitch(pitchSpecHz(tuning, { ...v.pitch, bendCents: v.pitch.bendCents + v.walk.bendCents }), 0.25);
        v.handle.setGesture({ radius: v.walk.brightness, velocity: 0 });
      }
    }
    let sounding = 0;
    for (const v of this.voices.values()) if (!v.handle.releasing) sounding++;
    return sounding;
  }

  // ------------------------------------------------------------ helpers
  private local(e: PointerEvent): { x: number; y: number } {
    const r = this.el?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  }
  private hit(x: number, y: number): ZoneHit {
    const tuning = this.deps.getTuning();
    return hitTest(this.deps.getLayout(), tuning.scale.semitones.length, x, y);
  }
}
